import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormApi } from 'final-form';
import { Box, Typography } from '@mui/material';

import { ConfirmDialog, LoadingOverlay } from '@occc/ui-design-system';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import OfferTypeSelectField from '@occc-app/routes/CampaignComponents/components/OfferTypeSelect';
import { ChallengeType, challengeTypes } from '@occc-app/routes/Challenge/constants';
import { AwardType, Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferFilter, Offer_Engagement_Type, User_Role } from '@occc/graphql';
import { EarningMechanismAwardSelector } from '@occc-app/offerHelpers/components/earning-mechanism-award-selector';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { VoucherExpirationDaysRedemption } from '@occc-app/routes/CampaignComponents/VoucherExpirationDaysRedemption';
import { EitherActivationOrAwardCount } from '@occc-app/routes/Promotion/components/PromotionConfigureForm/EitherActivationOrAwardCountProps';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { useUserSelector } from '@occc-app/App/AppContext';
import { StayVisibleWhileInProgress } from '@occc-app/routes/CampaignComponents/components/StayVisibleWhileInProgress';
import { useConfig } from '@occc/config';
import {
  AwardRestrictions,
  AwardRestrictionsAwardTypeWatcher,
  FulfillmentChannelConstraint,
  FulfillmentWindow,
  MinimumPurchaseConstraints,
  PaymentMethodConstraint,
  RequiresActivationConstraintWithLimits,
} from '@occc/feat-constraints';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';

import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { OfferCampaignField } from '@occc-app/routes/Offer/components/OfferCampaignField/offer-campaign-field.component';
import { OfferFulfillmentPriorityField } from '@occc-app/offerHelpers/components/OfferFulfillmentPriorityField';
import { ChallengeFormValuesWatcher } from './ChallengeFormValuesWatcher';
import {
  getIsProgressPerPeriodLimitSupported,
  getIsProgressPerTransactionLimitSupported,
  getIsMinimumPurchaseConstraintSupported,
  getIsTransactionLimitSupported,
} from './utils/challenge.utils';
import { getIsEarningMechanismDirty } from './utils/earningMechanismOperations';

import { messages } from '../../messages';
import { validateConfiguration } from '../../utils';
import { ProductChallengeConfiguration } from '../ProductChallengeConfiguration';
import { VisitChallengeConfiguration } from '../VisitChallengeConfiguration';
import { ChallengeConfigureFormValues, ChallengeConfigureFormInitialValues } from '../../types';
import { CustomData } from '../../../CampaignComponents/components/CustomData/CustomData';
import { configureFormCustomDataSubtitle } from '../../../../offerHelpers/copies';
import { ScanDataConfiguration } from '../../../CampaignComponents/components/ScanDataConfiguration/ScanDataConfiguration';

type ConfigureFormProps = {
  onSubmit: (
    values: ChallengeConfigureFormValues,
    form: FormApi<ChallengeConfigureFormValues, ChallengeConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: ChallengeConfigureFormInitialValues;
  makeOffersFilter: (search: string) => OfferFilter;
  loading: boolean;
  currentFlowType: Flow_Types;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export const ConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  makeOffersFilter,
  loading,
  currentFlowType,
  isFormEditable,
  onNextStepButtonClick,
}: ConfigureFormProps) => {
  const [isResetProgressDialogOpen, setIsResetProgressDialogOpen] = React.useState(false);
  const isOrderAheadEnabled = useConfig('orderAheadConfig').enabled;
  const user = useUserSelector();
  const isEditCampaignEnabled = isFormEditable && user?.userRole === User_Role.Admin;

  const renderForm: FormRenderProps<any>['render'] = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      form,
      dirty,
    }: FormRenderProps<ChallengeConfigureFormValues>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const handleSubmitWithProgressGuard: typeof handleSubmit = (event) => {
        const { dirtyFields, hasValidationErrors } = form.getState();
        const isDestructiveEdit =
          currentFlowType === Flow_Types.Update &&
          !hasValidationErrors &&
          getIsEarningMechanismDirty(dirtyFields);

        if (isDestructiveEdit) {
          event?.preventDefault?.();
          setIsResetProgressDialogOpen(true);
          return;
        }

        return handleSubmit(event);
      };

      const handleResetProgressDialogClose = () => {
        setIsResetProgressDialogOpen(false);
      };

      const handleResetProgressDialogConfirm = () => {
        setIsResetProgressDialogOpen(false);
        handleSubmit();
      };

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      const awardVouchers =
        values.award?.awardType === AwardType.AwardVouchers
          ? values.award.awardVouchers
          : undefined;
      const isVoucherRedemptionShown = values.award?.awardType === AwardType.AwardVouchers;
      const isVoucherRedemptionApplicable =
        awardVouchers?.voucheredOffer?.engagementType === Offer_Engagement_Type.Vouchered;
      const isVoucherRedemptionEnabled = awardVouchers?.isEnabled ?? false;

      const isVarietyChallengeOverTime =
        values.challengeType === ChallengeType.ProductChallenge &&
        values.productChallenge?.earningMechanism === 'varietyPurchaseOverTime';
      const isMinimumPurchaseConstraintSupported = getIsMinimumPurchaseConstraintSupported(
        values.challengeType,
        values.productChallenge?.earningMechanism,
      );
      const isAwardRestrictionsShown = values.award?.awardType === AwardType.AwardVouchers;

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmitWithProgressGuard}>
          <OfferEarningMechanismConfigurationSection>
            <OfferTypeSelectField
              isChangeButtonVisible={isFormEditable}
              items={challengeTypes}
              name='challengeType'
            />
            {values.challengeType && (
              <>
                <Box mb={2}>
                  <OfferNameAndDescriptionConfiguration entityName='Challenge'>
                    {currentFlowType === Flow_Types.Update && (
                      <OfferCampaignField canEdit={isEditCampaignEnabled} />
                    )}
                  </OfferNameAndDescriptionConfiguration>
                  <OfferFulfillmentPriorityField mt={2} />
                </Box>
                <EarningMechanismAwardSelector
                  buttonTypes={[AwardType.AwardPoints, AwardType.AwardVouchers]}
                  makeOffersFilter={makeOffersFilter}
                  fieldNamePrefix='award.'
                  values={{
                    awardType: values.award?.awardType,
                  }}
                />
                {values.challengeType === ChallengeType.ProductChallenge && (
                  <ProductChallengeConfiguration />
                )}
                {values.challengeType === ChallengeType.VisitChallenge && (
                  <VisitChallengeConfiguration />
                )}
              </>
            )}
          </OfferEarningMechanismConfigurationSection>

          {values.challengeType && (
            <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
              <OfferConfigurationSection title='Limits'>
                <AwardCount
                  values={values}
                  shouldShowTransactionLimit={getIsTransactionLimitSupported(
                    values.challengeType,
                    values.productChallenge?.earningMechanism,
                  )}
                  shouldShowProgressPerTransactionLimit={getIsProgressPerTransactionLimitSupported(
                    values.challengeType,
                    values.productChallenge?.earningMechanism,
                  )}
                  shouldShowProgressPerPeriodLimit={getIsProgressPerPeriodLimitSupported(
                    values.challengeType,
                  )}
                />
              </OfferConfigurationSection>

              <OfferConfigurationSection title='Requirements'>
                {isVoucherRedemptionShown && (
                  <VoucherExpirationDaysRedemption
                    isVoucherRedemptionEnabled={isVoucherRedemptionEnabled}
                    isVoucherRedemptionApplicable={isVoucherRedemptionApplicable}
                    showAvatar
                    fieldNamePrefix='award'
                  />
                )}
                {values.challengeType === ChallengeType.ProductChallenge && (
                  <PaymentMethodConstraint
                    values={values}
                    isPaymentInstrumentConstraintEnabled={false}
                  />
                )}
                <FulfillmentWindow values={values} />
                {isMinimumPurchaseConstraintSupported && (
                  <MinimumPurchaseConstraints values={values} />
                )}
                <RequiresActivationConstraintWithLimits values={values.activation} />

                {isOrderAheadEnabled && (
                  <FulfillmentChannelConstraint values={values.fulfillmentChannelConstraint} />
                )}

                {isVarietyChallengeOverTime && (
                  <StayVisibleWhileInProgress name='productChallenge.stayVisibleWhileInProgress.isEnabled' />
                )}
                {/*region Award Restrictions*/}
                {isAwardRestrictionsShown && <AwardRestrictions />}
                <AwardRestrictionsAwardTypeWatcher
                  awardTypeFieldName='award.awardType'
                  form={form}
                />
                {/*endregion*/}
              </OfferConfigurationSection>

              <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
                <CustomData values={values.customData} subtitle={configureFormCustomDataSubtitle} />
                <ScanDataConfiguration
                  values={values.scanDataConfiguration}
                  submitting={submitting}
                />
              </OfferConfigurationSection>
            </OfferConfigurationWizardFormLayout>
          )}

          <ChallengeFormValuesWatcher values={values} form={form} />

          <EitherActivationOrAwardCount form={form} />

          <ConfirmDialog
            dialogTitle={messages.resetProgressDialogTitle}
            open={isResetProgressDialogOpen}
            onClose={handleResetProgressDialogClose}
            maxWidth='sm'
            confirmButtonText={messages.resetProgressDialogConfirmButtonText}
            confirmButtonProps={{ onClick: handleResetProgressDialogConfirm, color: 'error' }}
          >
            <Typography>{messages.resetProgressDialogMessage}</Typography>
          </ConfirmDialog>
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      onNextStepButtonClick,
      makeOffersFilter,
      onCancelClick,
      isResetProgressDialogOpen,
      isOrderAheadEnabled,
      isEditCampaignEnabled,
    ],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<ChallengeConfigureFormValues, ChallengeConfigureFormInitialValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{
            ...arrayMutators,
            setFieldValue: (args, state, utils) => {
              // name, value
              utils.changeValue(state, args[0], () => args[1]);
            },
          }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
